const DEFAULT_ATHENA_API_URL = 'https://athenanet.athenahealth.com'

type GenerateAthenaEmrLinkParams = {
  patientId: string
  tableSpace: string
  athenaApiUrl?: string | null
}

/**
 * Maps Athena tablespace/practice ids to the URL segment expected by Athena EMR.
 * Extend this as we onboard additional tablespaces from backend payloads.
 */
const ATHENA_TABLESPACE_TO_URL_PARAM: Record<string, string> = {
  // Example mapping placeholders; fallback is the raw tablespace.
  // '882499': '882499',
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '')
}

export function getAthenaUrlParam(tableSpace: string): string {
  const normalized = tableSpace.trim()
  return ATHENA_TABLESPACE_TO_URL_PARAM[normalized] ?? normalized
}

/**
 * Builds an Athena chart URL.
 *
 * Default template:
 *   {base}/1/{athenaUrlParam}/chart/{patientId}
 *
 * Override with `VITE_ATHENA_EMR_URL_TEMPLATE` if backend specifies a different route:
 *   e.g. "https://host/1/{athenaUrlParam}/chart/{patientId}"
 */
export function generateAthenaEmrLink({
  athenaApiUrl,
  tableSpace,
  patientId,
}: GenerateAthenaEmrLinkParams): string {
  const normalizedPatientId = patientId.trim()
  if (!normalizedPatientId) {
    throw new Error('Cannot build Athena link: missing patientId.')
  }

  const normalizedTableSpace = tableSpace.trim()
  if (!normalizedTableSpace) {
    throw new Error('Cannot build Athena link: missing tableSpace.')
  }

  const athenaUrlParam = getAthenaUrlParam(normalizedTableSpace)
  const baseUrl = normalizeBaseUrl(
    athenaApiUrl?.trim() || import.meta.env.VITE_ATHENA_API_URL || DEFAULT_ATHENA_API_URL,
  )

  const template = import.meta.env.VITE_ATHENA_EMR_URL_TEMPLATE?.trim()
  if (template) {
    return template
      .replaceAll('{base}', baseUrl)
      .replaceAll('{athenaUrlParam}', encodeURIComponent(athenaUrlParam))
      .replaceAll('{patientId}', encodeURIComponent(normalizedPatientId))
  }

  return `${baseUrl}/1/${encodeURIComponent(athenaUrlParam)}/chart/${encodeURIComponent(
    normalizedPatientId,
  )}`
}

